import { useState } from 'react';
import { motion } from 'framer-motion';

import { Database, Upload, RefreshCw, AlertTriangle, Loader2, Check, Package, Pill, ShieldAlert, ScrollText, ClipboardCheck, FolderOpen, ShoppingCart } from 'lucide-react';
import { ALL_SEED_PRODUCTS, SEED_BUNDLES, SEED_SAFETY_RULES } from '../../../data/formulary';
import { SEED_REGULATORY_ALERTS, SEED_REGULATORY_POLICIES, SEED_REGULATORY_CAMPAIGNS } from '../../../data/regulatory';
import { SEED_COMPLIANCE_ITEMS } from '../../../data/dataops';
import { ALL_SEED_TEMPLATES, SEED_CATEGORIES, DEFAULT_BRAND_CONFIG, SEED_OWNER_PROFILE } from '../../../data/docops';
import { SEED_TEST_ORDERS } from '../../../data/orders/seedTestOrders';
import { seedFormulary, regenerateManifest } from '../../../services/firestore/formularyService';
import { seedRegulatoryAll } from '../../../services/firestore/regulatoryService';
import { seedComplianceItems } from '../../../services/firestore/dataopsService';
import { seedTemplates, seedCategories, seedBrand, seedOwnerProfile } from '../../../services/firestore/docopsService';
import { seedTestOrders } from '../../../services/firestore/orderService';
import { User } from 'lucide-react';

export function SeedPage() {
  const [progress, setProgress] = useState<string[]>([]);
  const [seeding, setSeeding] = useState(false);
  const [seedingRegulatory, setSeedingRegulatory] = useState(false);
  const [regenerating, setRegenerating] = useState(false);
  const [result, setResult] = useState<{ products: number; bundles: number; rules: number } | null>(null);
  const [regulatoryResult, setRegulatoryResult] = useState<{ alerts: number; policies: number; campaigns: number } | null>(null);
  const [seedingDataOps, setSeedingDataOps] = useState(false);
  const [dataOpsResult, setDataOpsResult] = useState<number | null>(null);
  const [seedingDocTemplates, setSeedingDocTemplates] = useState(false);
  const [docTemplatesResult, setDocTemplatesResult] = useState<number | null>(null);
  const [seedingDocCategories, setSeedingDocCategories] = useState(false);
  const [docCategoriesResult, setDocCategoriesResult] = useState<number | null>(null);
  const [seedingDocBrand, setSeedingDocBrand] = useState(false);
  const [docBrandResult, setDocBrandResult] = useState<number | null>(null);
  const [seedingOwner, setSeedingOwner] = useState(false);
  const [ownerResult, setOwnerResult] = useState<boolean>(false);
  const [seedingOrders, setSeedingOrders] = useState(false);
  const [ordersResult, setOrdersResult] = useState<number | null>(null);

  async function handleSeed() {
    if (!window.confirm(
      'This will overwrite existing formulary data in Firestore. Are you sure you want to proceed?'
    )) {
      return;
    }

    try {
      setSeeding(true);
      setProgress([]);
      setResult(null);

      const res = await seedFormulary(
        ALL_SEED_PRODUCTS,
        SEED_BUNDLES,
        SEED_SAFETY_RULES,
        (msg) => setProgress((prev) => [...prev, msg]),
      );

      setProgress((prev) => [...prev, 'Seeding complete!']);
      setResult(res);
    } catch (err) {
      setProgress((prev) => [...prev, `Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
    } finally {
      setSeeding(false);
    }
  }

  async function handleRegenerate() {
    try {
      setRegenerating(true);
      setProgress((prev) => [...prev, 'Regenerating manifest...']);
      const manifest = await regenerateManifest();
      setProgress((prev) => [
        ...prev,
        `Manifest regenerated: ${manifest.productCount} products, ${manifest.bundleCount} bundles`,
      ]);
    } catch (err) {
      setProgress((prev) => [...prev, `Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
    } finally {
      setRegenerating(false);
    }
  }

  async function handleSeedRegulatory() {
    if (!window.confirm(
      'This will overwrite existing regulatory data in Firestore (alerts, policies, campaigns). Are you sure?'
    )) {
      return;
    }

    try {
      setSeedingRegulatory(true);
      setRegulatoryResult(null);
      setProgress((prev) => [...prev, 'Seeding regulatory data...']);

      const res = await seedRegulatoryAll(
        SEED_REGULATORY_ALERTS,
        SEED_REGULATORY_POLICIES,
        SEED_REGULATORY_CAMPAIGNS,
        (msg) => setProgress((prev) => [...prev, msg]),
      );

      setProgress((prev) => [...prev, `Regulatory seeding complete! (${res.alerts} alerts, ${res.policies} policies, ${res.campaigns} campaigns)`]);
      setRegulatoryResult(res);
    } catch (err) {
      setProgress((prev) => [...prev, `Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
    } finally {
      setSeedingRegulatory(false);
    }
  }

  async function handleSeedDataOps() {
    if (!window.confirm(
      'This will overwrite existing compliance items in Firestore. Are you sure?'
    )) {
      return;
    }

    try {
      setSeedingDataOps(true);
      setDataOpsResult(null);
      setProgress((prev) => [...prev, 'Seeding compliance items...']);

      const count = await seedComplianceItems(
        SEED_COMPLIANCE_ITEMS,
        (msg) => setProgress((prev) => [...prev, msg]),
      );

      setProgress((prev) => [...prev, `Data Ops seeding complete! (${count} compliance items)`]);
      setDataOpsResult(count);
    } catch (err) {
      setProgress((prev) => [...prev, `Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
    } finally {
      setSeedingDataOps(false);
    }
  }

  async function handleSeedDocTemplates() {
    if (!window.confirm(
      'This will overwrite existing document templates in Firestore. Are you sure?'
    )) return;
    try {
      setSeedingDocTemplates(true);
      setDocTemplatesResult(null);
      setProgress((prev) => [...prev, 'Seeding document templates...']);
      const count = await seedTemplates(
        ALL_SEED_TEMPLATES,
        (msg) => setProgress((prev) => [...prev, msg]),
      );
      setProgress((prev) => [...prev, `Doc template seeding complete! (${count} templates)`]);
      setDocTemplatesResult(count);
    } catch (err) {
      setProgress((prev) => [...prev, `Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
    } finally {
      setSeedingDocTemplates(false);
    }
  }

  async function handleSeedDocCategories() {
    if (!window.confirm(
      'This will overwrite existing document categories in Firestore. Are you sure?'
    )) return;
    try {
      setSeedingDocCategories(true);
      setDocCategoriesResult(null);
      setProgress((prev) => [...prev, 'Seeding document categories...']);
      const count = await seedCategories(
        SEED_CATEGORIES,
        (msg) => setProgress((prev) => [...prev, msg]),
      );
      setProgress((prev) => [...prev, `Doc category seeding complete! (${count} categories)`]);
      setDocCategoriesResult(count);
    } catch (err) {
      setProgress((prev) => [...prev, `Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
    } finally {
      setSeedingDocCategories(false);
    }
  }

  async function handleSeedOwnerProfile() {
    if (!window.confirm(
      'This will seed Stan\'s owner profile to Firestore. Are you sure?'
    )) return;
    try {
      setSeedingOwner(true);
      setOwnerResult(false);
      setProgress((prev) => [...prev, 'Seeding owner profile...']);
      await seedOwnerProfile(
        SEED_OWNER_PROFILE,
        (msg) => setProgress((prev) => [...prev, msg]),
      );
      setProgress((prev) => [...prev, 'Owner profile seeding complete!']);
      setOwnerResult(true);
    } catch (err) {
      setProgress((prev) => [...prev, `Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
    } finally {
      setSeedingOwner(false);
    }
  }

  async function handleSeedOrders() {
    if (!window.confirm(
      `This will write ${SEED_TEST_ORDERS.length} simulated test orders to Firestore. Existing orders with the same IDs will be overwritten. Are you sure?`
    )) return;
    try {
      setSeedingOrders(true);
      setOrdersResult(null);
      setProgress((prev) => [...prev, `Seeding ${SEED_TEST_ORDERS.length} test orders...`]);
      const count = await seedTestOrders(
        SEED_TEST_ORDERS as unknown as Record<string, unknown>[],
        (msg) => setProgress((prev) => [...prev, msg]),
      );
      setProgress((prev) => [...prev, `Order seeding complete! (${count} orders)`]);
      setOrdersResult(count);
    } catch (err) {
      setProgress((prev) => [...prev, `Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
    } finally {
      setSeedingOrders(false);
    }
  }

  async function handleSeedDocBrand() {
    if (!window.confirm(
      'This will create the HemoLogix brand and initialize 86 document stubs. Are you sure?'
    )) return;
    try {
      setSeedingDocBrand(true);
      setDocBrandResult(null);
      setProgress((prev) => [...prev, 'Seeding HemoLogix brand + document stubs...']);
      const count = await seedBrand(
        DEFAULT_BRAND_CONFIG,
        ALL_SEED_TEMPLATES,
        (msg) => setProgress((prev) => [...prev, msg]),
      );
      setProgress((prev) => [...prev, `Brand seeding complete! (${count} document stubs)`]);
      setDocBrandResult(count);
    } catch (err) {
      setProgress((prev) => [...prev, `Error: ${err instanceof Error ? err.message : 'Unknown error'}`]);
    } finally {
      setSeedingDocBrand(false);
    }
  }

  return (
    <div className="max-w-3xl">
      <div className="mb-6">
        <h2 className="text-3xl font-display text-gold">Seed Formulary Data</h2>
        <p className="text-base text-text-muted mt-1">
          Import seed data into Firestore from the local seed files.
        </p>
      </div>

      {/* Warning */}
      <div
        className="anim-fade-in-up bg-yellow-50 border border-yellow-200 rounded-xl p-4 mb-6 flex items-start gap-3"
      >
        <AlertTriangle size={20} className="text-yellow-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-base font-medium text-yellow-700">Warning: This will overwrite existing data</p>
          <p className="text-sm text-yellow-600 mt-1">
            Seeding will replace all products, bundles, and safety rules in Firestore with the local seed data.
            Existing customizations will be lost.
          </p>
        </div>
      </div>

      {/* Seed counts */}
      <div className="grid grid-cols-1 sm:grid-cols-5 gap-4 mb-6">
        <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-gold/10">
              <Pill size={18} className="text-gold" />
            </div>
            <div>
              <p className="text-3xl font-bold font-display">{ALL_SEED_PRODUCTS.length}</p>
              <p className="text-sm text-text-muted">Products</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-accent/10">
              <Package size={18} className="text-accent-light" />
            </div>
            <div>
              <p className="text-3xl font-bold font-display">{SEED_BUNDLES.length}</p>
              <p className="text-sm text-text-muted">Bundles</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-green-50">
              <ShieldAlert size={18} className="text-optimal" />
            </div>
            <div>
              <p className="text-3xl font-bold font-display">{SEED_SAFETY_RULES.length}</p>
              <p className="text-sm text-text-muted">Safety Rules</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-red-50">
              <ScrollText size={18} className="text-accent" />
            </div>
            <div>
              <p className="text-3xl font-bold font-display">{SEED_REGULATORY_ALERTS.length}</p>
              <p className="text-sm text-text-muted">Regulatory Alerts</p>
            </div>
          </div>
        </div>
        <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-blue-50">
              <ClipboardCheck size={18} className="text-blue-600" />
            </div>
            <div>
              <p className="text-3xl font-bold font-display">{SEED_COMPLIANCE_ITEMS.length}</p>
              <p className="text-sm text-text-muted">Compliance Items</p>
            </div>
          </div>
        </div>
      </div>

      {/* Summary line */}
      <div className="bg-bg-card border border-bg-tertiary rounded-xl p-4 mb-6">
        <p className="text-base text-text-secondary">
          Will write <span className="text-gold font-medium">{ALL_SEED_PRODUCTS.length} products</span>,{' '}
          <span className="text-accent-light font-medium">{SEED_BUNDLES.length} bundles</span>, and{' '}
          <span className="text-optimal font-medium">{SEED_SAFETY_RULES.length} safety rules</span> to Firestore.
        </p>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <button
          onClick={handleSeed}
          disabled={seeding}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-accent hover:bg-accent/80 text-white rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {seeding ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <Upload size={18} />
          )}
          Seed to Firestore
        </button>
        <button
          onClick={handleRegenerate}
          disabled={regenerating}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {regenerating ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <RefreshCw size={18} />
          )}
          Regenerate Manifest
        </button>
        <button
          onClick={handleSeedRegulatory}
          disabled={seedingRegulatory}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {seedingRegulatory ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <ScrollText size={18} />
          )}
          Seed Regulatory Data
        </button>
        <button
          onClick={handleSeedDataOps}
          disabled={seedingDataOps}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {seedingDataOps ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <ClipboardCheck size={18} />
          )}
          Seed Data Ops
        </button>
        <button
          onClick={handleSeedDocTemplates}
          disabled={seedingDocTemplates}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {seedingDocTemplates ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <FolderOpen size={18} />
          )}
          Seed Doc Templates
        </button>
        <button
          onClick={handleSeedDocCategories}
          disabled={seedingDocCategories}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {seedingDocCategories ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <FolderOpen size={18} />
          )}
          Seed Doc Categories
        </button>
        <button
          onClick={handleSeedDocBrand}
          disabled={seedingDocBrand}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {seedingDocBrand ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <FolderOpen size={18} />
          )}
          Seed HemoLogix Brand
        </button>
        <button
          onClick={handleSeedOwnerProfile}
          disabled={seedingOwner}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-bg-elevated hover:bg-bg-tertiary border border-bg-tertiary text-text-primary rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {seedingOwner ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <User size={18} />
          )}
          Seed Owner Profile
        </button>
        <button
          onClick={handleSeedOrders}
          disabled={seedingOrders}
          className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-gold hover:bg-gold/80 text-bg-deep rounded-lg text-base font-medium transition-colors disabled:opacity-50"
        >
          {seedingOrders ? (
            <Loader2 size={18} className="animate-spin" />
          ) : (
            <ShoppingCart size={18} />
          )}
          Seed Test Orders ({SEED_TEST_ORDERS.length})
        </button>
      </div>

      {/* Progress log */}
      {progress.length > 0 && (
        <div className="bg-bg-card border border-bg-tertiary rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-bg-tertiary flex items-center gap-2">
            <Database size={16} className="text-text-muted" />
            <h3 className="text-sm font-medium text-text-secondary">Progress Log</h3>
          </div>
          <div className="p-4 max-h-64 overflow-y-auto font-mono text-sm space-y-1">
            {progress.map((msg, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -8 }}
                animate={{ opacity: 1, x: 0 }}
                className="flex items-start gap-2"
              >
                <Check size={12} className="text-optimal flex-shrink-0 mt-0.5" />
                <span className="text-text-secondary">{msg}</span>
              </motion.div>
            ))}
            {seeding && (
              <div className="flex items-center gap-2 text-gold">
                <Loader2 size={12} className="animate-spin" />
                <span>Working...</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Result summary */}
      {result && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <p className="text-base font-medium text-optimal">Seeding Complete</p>
          <p className="text-sm text-text-secondary mt-1">
            Successfully wrote {result.products} products, {result.bundles} bundles, and {result.rules} safety rules.
          </p>
        </motion.div>
      )}

      {regulatoryResult !== null && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <p className="text-base font-medium text-optimal">Regulatory Seeding Complete</p>
          <p className="text-sm text-text-secondary mt-1">
            Successfully wrote {regulatoryResult.alerts} alerts, {regulatoryResult.policies} policies, and {regulatoryResult.campaigns} campaigns.
          </p>
        </motion.div>
      )}

      {dataOpsResult !== null && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <p className="text-base font-medium text-optimal">Data Ops Seeding Complete</p>
          <p className="text-sm text-text-secondary mt-1">
            Successfully wrote {dataOpsResult} compliance items.
          </p>
        </motion.div>
      )}

      {docTemplatesResult !== null && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <p className="text-base font-medium text-optimal">Doc Templates Seeding Complete</p>
          <p className="text-sm text-text-secondary mt-1">
            Successfully wrote {docTemplatesResult} document templates.
          </p>
        </motion.div>
      )}

      {docCategoriesResult !== null && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <p className="text-base font-medium text-optimal">Doc Categories Seeding Complete</p>
          <p className="text-sm text-text-secondary mt-1">
            Successfully wrote {docCategoriesResult} document categories.
          </p>
        </motion.div>
      )}

      {docBrandResult !== null && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <p className="text-base font-medium text-optimal">HemoLogix Brand Seeding Complete</p>
          <p className="text-sm text-text-secondary mt-1">
            Successfully created brand and {docBrandResult} document stubs.
          </p>
        </motion.div>
      )}

      {ownerResult && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <p className="text-base font-medium text-optimal">Owner Profile Seeding Complete</p>
          <p className="text-sm text-text-secondary mt-1">
            Successfully seeded Stan's owner profile to Firestore.
          </p>
        </motion.div>
      )}

      {ordersResult !== null && (
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="mt-4 bg-green-50 border border-green-200 rounded-xl p-4"
        >
          <p className="text-base font-medium text-optimal">Test Orders Seeding Complete</p>
          <p className="text-sm text-text-secondary mt-1">
            Successfully wrote {ordersResult} simulated orders. Financial dashboard will now show data.
          </p>
        </motion.div>
      )}
    </div>
  );
}
